import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import FormTextField from 'components/IAP/formFields/FormTextField';
import type { InternalLayer } from 'hooks/internalLayer/types';
import { useListInternalLayerVersionsQuery } from 'hooks/internalLayerVersion/useListInternalLayerVersionsQuery';
import type { ListInternalLayerVersionsQuery } from 'hooks/internalLayerVersion/types';
import {
  BOOM_RESOURCES,
  type LocationPolygonProperties,
  type LocationInput,
} from '../../../../types/locations';
import { useUscgMapContext } from './UscgMapContext';
import { type UscgSymbol } from './utils/uscgSymbology';
import { usePrimaryPoint } from './utils/usePrimaryPoint';
import { v4 as uuidv4 } from 'uuid';
import FormSymbologySelect from './FormSymbologySelect';
import { getLatLongFromGeometry } from './utils/getLatLongFromGeometry';

type CreateFeatureFormProps = {
  internalLayer?: InternalLayer | null;
  onClose: () => void;
  onDiscard?: () => void;
  onSubmit: ({
    location,
    pendingGraphicId,
  }: {
    location: LocationInput;
    pendingGraphicId?: string;
  }) => Promise<void> | void;
};

export function CreateFeatureForm({
  internalLayer,
  onClose,
  onDiscard,
  onSubmit,
}: CreateFeatureFormProps) {
  const {
    startDrawing,
    setPolygonDrawingColor,
    setPolygonDrawingOpacity,
    addPendingPoint,
    addPendingPolyline,
    addPendingPolygon,
    stopDrawing,
    symbolDrawingState,
    connectDrawingPoints,
    resetDrawing,
  } = useUscgMapContext();

  const addObjectForm = useForm({
    defaultValues: {
      featureName: '',
      latitude: '',
      longitude: '',
      description: '',
      symbology: '',
      polygonColor: '#1e40af',
      polygonOpacity: '25',
    },
  });

  const { setValue, reset, handleSubmit, getValues, watch } = addObjectForm;
  const [selectedType, setSelectedType] = useState<UscgSymbol | null>(null);
  const [hasSaved, setHasSaved] = useState(false);
  const polygonColor = watch('polygonColor');
  const polygonOpacity = watch('polygonOpacity');

  const versionsQuery: ListInternalLayerVersionsQuery = {
    filter: {
      internalLayerId: internalLayer?.id,
      status: 'DRAFT',
    },
  };

  const { data: versionsData } = useListInternalLayerVersionsQuery(
    versionsQuery,
    { enabled: internalLayer != null }
  );
  const draftVersion = versionsData?.data?.[0];

  const isBoomSelected = selectedType === 'proposedBoom';
  const isOilSpillSelected = selectedType === 'oilSpill';
  const isPolygonSelected = selectedType === 'customPolygon';
  const boomHasMultiplePoints =
    isBoomSelected && symbolDrawingState.points.length >= 2;

  const primaryPoint = usePrimaryPoint({
    selectedType,
    centroidTypes: ['proposedBoom', 'oilSpill'],
  });

  useEffect(() => {
    if (isPolygonSelected) {
      const center = getLatLongFromGeometry(symbolDrawingState.polygon);
      if (center) {
        setValue('latitude', String(center.latitude));
        setValue('longitude', String(center.longitude));
      }
      return;
    }

    if (primaryPoint) {
      setValue('latitude', String(primaryPoint.latitude));
      setValue('longitude', String(primaryPoint.longitude));
    }
  }, [isPolygonSelected, primaryPoint, setValue, symbolDrawingState.polygon]);

  const handleSymbologyChange = (type: string | null) => {
    if (type) {
      if (type === 'customPolygon') {
        setPolygonDrawingColor(getValues('polygonColor'));
        const opacityPercent = Number(getValues('polygonOpacity'));
        const clampedPercent = Number.isNaN(opacityPercent)
          ? 25
          : Math.max(0, Math.min(100, opacityPercent));
        setPolygonDrawingOpacity(clampedPercent / 100);
      }
      startDrawing(type as UscgSymbol);
    } else {
      stopDrawing();
    }
    setValue('latitude', '');
    setValue('longitude', '');
  };

  const handleConnectPointsClick = () => {
    if (!selectedType || selectedType !== 'proposedBoom') return;
    connectDrawingPoints(selectedType);
  };

  const handleClearClick = () => {
    // Clear temporary drawing state and re-start drawing for the
    // currently selected symbology (if any).
    resetDrawing();
    setValue('latitude', '');
    setValue('longitude', '');

    if (selectedType) {
      handleSymbologyChange(selectedType);
    }
  };

  const handleSave = handleSubmit(async (data) => {
    if (!selectedType) return;

    const { points, line } = symbolDrawingState;
    const isNewLayer = internalLayer == null;
    const versionId = draftVersion?.id ?? null;

    const createdAt = new Date().toISOString();
    const locationPoints = points.map((p, idx) => ({
      id: p.id,
      longitude: p.longitude,
      latitude: p.latitude,
      order: p.order ?? idx,
      createdAt,
    }));

    let locationInput: LocationInput;
    // eslint-disable-next-line unused-imports/no-unused-vars
    let runPendingUpload = (pendingGraphicId: string) => {};

    if (data.symbology === 'proposedBoom') {
      const resourceType = line
        ? BOOM_RESOURCES.COMPLETED
        : BOOM_RESOURCES.PROPOSED;
      locationInput = {
        name: data.featureName,
        description: data.description,
        geometry: {
          type: 'LineString',
          coordinates: locationPoints.map((p) => [p.longitude, p.latitude]),
        },
        resourceType,
        properties: {
          points: locationPoints,
          line: line
            ? {
                ...line,
                points: locationPoints,
                createdAt,
              }
            : null,
        },
        internalLayerVersionId: versionId,
      };

      runPendingUpload = (pendingGraphicId: string) => {
        locationPoints.forEach((p) =>
          addPendingPoint({
            symbolKey: 'proposedBoom',
            point: p,
            pendingGraphicId,
          })
        );
        if (line) {
          addPendingPolyline({
            symbolKey: 'proposedBoom',
            points: locationPoints,
            pendingGraphicId,
          });
        }
      };
    } else if (data.symbology === 'oilSpill') {
      locationInput = {
        name: data.featureName,
        description: data.description,
        resourceType: 'oilSpill',
        geometry: {
          type: 'LineString',
          coordinates: locationPoints.map((p) => [p.longitude, p.latitude]),
        },
        properties: { points: locationPoints, line: null },
        internalLayerVersionId: versionId,
      };

      runPendingUpload = (pendingGraphicId: string) => {
        addPendingPolyline({
          symbolKey: 'oilSpill',
          points: locationPoints,
          pendingGraphicId,
        });
      };
    } else if (data.symbology === 'customPolygon') {
      const polygon = symbolDrawingState.polygon;
      if (!polygon) return;

      const properties: LocationPolygonProperties = {
        polygonColor: data.polygonColor,
        polygonOpacity: Math.max(
          0,
          Math.min(1, Number(data.polygonOpacity || '25') / 100)
        ),
      };

      locationInput = {
        name: data.featureName,
        description: data.description,
        resourceType: selectedType,
        properties,
        geometry: polygon,
        internalLayerVersionId: versionId,
      };

      runPendingUpload = (pendingGraphicId: string) => {
        addPendingPolygon({
          geometry: polygon,
          colorHex: data.polygonColor,
          opacity: Math.max(
            0,
            Math.min(1, Number(data.polygonOpacity || '25') / 100)
          ),
          pendingGraphicId,
        });
      };
    } else {
      const coords = {
        longitude: Number(data.longitude),
        latitude: Number(data.latitude),
      };
      locationInput = {
        name: data.featureName,
        description: data.description,
        geometry: {
          type: 'Point',
          coordinates: [coords.longitude, coords.latitude],
        },
        resourceType: selectedType,
        properties: coords,
        internalLayerVersionId: versionId,
      };

      runPendingUpload = (pendingGraphicId: string) => {
        addPendingPoint({
          symbolKey: selectedType,
          point: coords,
          pendingGraphicId,
        });
      };
    }

    let pendingGraphicId: string | undefined;
    if (isNewLayer) {
      pendingGraphicId = uuidv4();
      runPendingUpload(pendingGraphicId);
    }

    await onSubmit({
      location: locationInput,
      pendingGraphicId,
    });

    setHasSaved(true);
    stopDrawing();
    setSelectedType(null);
    reset();
    onClose();
  });

  const handleCancel = () => {
    // Cancel should discard any temporary graphics and form state.
    stopDrawing();
    setSelectedType(null);
    reset();

    // If layer hasn't been created yet, discard the last committed pending object.
    if (hasSaved) {
      setHasSaved(false);
      onDiscard?.();
    }

    onClose();
  };

  return (
    <FormProvider {...addObjectForm}>
      <div className="d-flex flex-column gap-1 mt-2 border border-white rounded p-4">
        <FormTextField
          title="Name"
          fieldName="featureName"
          rules={{ required: { value: true, message: 'Required' } }}
          variant="full"
        />
        <FormSymbologySelect
          excludeOptions={['completedBoom']}
          includeCustomPolygon
          onSymbologyChange={(newValue) => {
            setSelectedType(newValue);
            handleSymbologyChange(newValue);
          }}
        />
        <p className="text-muted small mb-0">
          {isPolygonSelected
            ? 'Click on the map to draw a polygon.'
            : 'Click on the map to add a point.'}
        </p>
        {isPolygonSelected && (
          <div className="mt-2 col-12">
            <div className="row g-2 align-items-end">
              <div className="col-6">
                <label className="form-label" htmlFor="polygonColor">
                  Polygon color
                </label>
                <div className="d-flex align-items-center gap-2">
                  <input
                    id="polygonColor"
                    type="color"
                    className="form-control form-control-color p-1"
                    title="Choose polygon color"
                    {...addObjectForm.register('polygonColor', {
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        setPolygonDrawingColor(e.target.value);
                      },
                    })}
                  />
                  <span className="text-muted small">
                    {String(polygonColor ?? '').toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="col-6">
                <label className="form-label" htmlFor="polygonOpacity">
                  Transparency
                </label>
                <div className="d-flex align-items-center gap-2">
                  <input
                    id="polygonOpacity"
                    type="range"
                    min="0"
                    max="100"
                    className="w-100"
                    title="Adjust polygon transparency"
                    {...addObjectForm.register('polygonOpacity', {
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        const percent = Number(e.target.value);
                        const clamped = Number.isNaN(percent)
                          ? 25
                          : Math.max(0, Math.min(100, percent));
                        setPolygonDrawingOpacity(clamped / 100);
                      },
                    })}
                  />
                  <span
                    className="text-muted small"
                    style={{ minWidth: '64px' }}
                  >
                    {`${Math.max(0, Math.min(100, Number(polygonOpacity || '25')))}%`}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        {(isBoomSelected || isOilSpillSelected) && (
          <div className="d-flex gap-2 mt-1">
            {isBoomSelected && (
              <StylishNewButton
                className="button--secondary"
                disabled={!boomHasMultiplePoints}
                onClick={handleConnectPointsClick}
              >
                Connect points
              </StylishNewButton>
            )}
            <StylishNewButton
              className="button--secondary"
              onClick={handleClearClick}
            >
              Clear
            </StylishNewButton>
          </div>
        )}
        <div className="row">
          <FormTextField
            title="Latitude"
            fieldName="latitude"
            variant="half"
            disabled
          />
          <FormTextField
            title="Longitude"
            fieldName="longitude"
            variant="half"
            disabled
          />
        </div>
        <FormTextField
          title="Description"
          fieldName="description"
          variant="full"
        />
        <div className="d-flex gap-2">
          <StylishNewButton
            className="button--primary align-self-start"
            disabled={
              !selectedType ||
              (!primaryPoint &&
                !(isPolygonSelected && !!symbolDrawingState.polygon))
            }
            onClick={handleSave}
          >
            Save
          </StylishNewButton>
          <StylishNewButton
            className="button--secondary"
            onClick={handleCancel}
          >
            Cancel
          </StylishNewButton>
        </div>
      </div>
    </FormProvider>
  );
}
